import { Link } from "@tanstack/react-router";
import {
  AgentsIcon,
  ArrowUpRightIcon,
  FileIcon,
  MessagesSquareIcon,
  ShieldCheckIcon,
  ZapIcon,
} from "lucide-react";
import { useMemo } from "react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";
import { usePrimaryEnvironmentId } from "../../state/environments";
import { Button } from "../ui/button";
import type { ActivityEvent } from "./activitiesTypes";
import type { ActivityDetailLinkId, ActivityDetailRouteLink } from "./activityDetailLinks";
import { collectActivitiesQuickAccess, type ActivityQuickAccessItem } from "./activityQuickAccess";
import { formatActivityTime } from "./activityPresentation";

export function ActivitiesQuickAccess({
  events,
  mockMode,
  selectedId,
  onSelect,
}: {
  readonly events: readonly ActivityEvent[];
  readonly mockMode: boolean;
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}) {
  const { t, locale } = useI18n();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const items = useMemo(
    () => collectActivitiesQuickAccess(events, primaryEnvironmentId),
    [events, primaryEnvironmentId],
  );

  if (items.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border/70 px-3 py-6 text-center text-xs text-muted-foreground">
        {t("activities.quickAccess.empty")}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {items.map((item) => {
        const selected = selectedId === item.sourceEventId;
        return (
          <li key={item.key}>
            <QuickAccessCard
              item={item}
              mockMode={mockMode}
              locale={locale}
              selected={selected}
              onSelect={() => onSelect(item.sourceEventId)}
            />
          </li>
        );
      })}
    </ul>
  );
}

function QuickAccessCard({
  item,
  mockMode,
  locale,
  selected,
  onSelect,
}: {
  readonly item: ActivityQuickAccessItem;
  readonly mockMode: boolean;
  readonly locale: string;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  const { t } = useI18n();

  if (item.kind === "file_label") {
    return (
      <button type="button" onClick={onSelect} className={cardShellClass(selected)}>
        <CardIcon id="file" />
        <CardBody
          title={t("activities.detail.file")}
          subtitle={item.label}
          meta={formatActivityTime(item.occurredAt, locale)}
        />
      </button>
    );
  }

  const { link } = item;
  const label = t(link.labelKey);
  const subtitle = link.subtitle?.trim();
  const Icon = linkIcon(link.id);

  if (mockMode) {
    return (
      <button
        type="button"
        onClick={onSelect}
        className={cn(cardShellClass(selected), "opacity-85")}
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border/60 bg-background">
          <Icon className="size-4 text-muted-foreground" aria-hidden />
        </span>
        <CardBody
          title={label}
          subtitle={subtitle}
          meta={formatActivityTime(item.occurredAt, locale)}
        />
      </button>
    );
  }

  return (
    <Button
      variant="ghost"
      className={cn(
        cardShellClass(selected),
        "h-auto justify-start font-normal sm:h-auto [:hover,[data-pressed]]:bg-muted/35",
      )}
      render={activityLinkRender(link)}
      onClick={onSelect}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/30">
        <Icon className="size-4 text-muted-foreground" aria-hidden />
      </span>
      <CardBody
        title={label}
        subtitle={subtitle}
        meta={formatActivityTime(item.occurredAt, locale)}
        showArrow
      />
    </Button>
  );
}

function cardShellClass(selected: boolean): string {
  return cn(
    "flex w-full items-center gap-3 rounded-xl border border-border/70 bg-card px-3 py-2.5 text-left transition-colors hover:bg-muted/35",
    selected && "border-primary/40 bg-muted/25 ring-1 ring-primary/25",
  );
}

function CardIcon({ id }: { readonly id: ActivityDetailLinkId }) {
  const Icon = linkIcon(id);
  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/30">
      <Icon className="size-4 text-muted-foreground" aria-hidden />
    </span>
  );
}

function CardBody({
  title,
  subtitle,
  meta,
  showArrow,
}: {
  readonly title: string;
  readonly subtitle?: string | undefined;
  readonly meta: string;
  readonly showArrow?: boolean;
}) {
  return (
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <span className="truncate">{title}</span>
        {showArrow ? (
          <ArrowUpRightIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        ) : null}
      </span>
      {subtitle ? (
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">{subtitle}</span>
      ) : null}
      <span className="mt-0.5 block text-[10px] tabular-nums text-muted-foreground/80">{meta}</span>
    </span>
  );
}

function linkIcon(id: ActivityDetailLinkId) {
  switch (id) {
    case "thread":
      return MessagesSquareIcon;
    case "agent":
      return AgentsIcon;
    case "workflow":
      return ZapIcon;
    case "allowance":
      return ShieldCheckIcon;
    case "file":
      return FileIcon;
    default:
      return ArrowUpRightIcon;
  }
}

function activityLinkRender(link: ActivityDetailRouteLink): React.ReactElement {
  switch (link.id) {
    case "thread": {
      const environmentId = link.params?.environmentId;
      const threadId = link.params?.threadId;
      if (!environmentId || !threadId) {
        return <Link to="/activities" />;
      }
      return <Link to="/$environmentId/$threadId" params={{ environmentId, threadId }} />;
    }
    case "agent": {
      const agentId = link.params?.agentId;
      if (!agentId) {
        return <Link to="/activities" />;
      }
      return <Link to="/agents/$agentId" params={{ agentId }} />;
    }
    case "workflow": {
      const id = link.params?.id;
      if (!id) {
        return <Link to="/activities" />;
      }
      return <Link to="/workflows/$id" params={{ id }} />;
    }
    case "allowance":
      return <Link to="/allowance" />;
    case "file": {
      const item = link.search?.item;
      if (!item) {
        return <Link to="/vault" />;
      }
      return <Link to="/vault" search={{ item }} />;
    }
    default:
      return <Link to="/activities" />;
  }
}
